import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Loader2, FileDown } from "lucide-react";
import { generateTunisiaPayslipPdf } from "../../utils/tunisiaPayslipPdf";

const SimplePayslipTemplate = ({ entry, companyInfo, formatCurrency }) => {
  const { t, i18n } = useTranslation();
  if (!entry) return null;
  const currentDate = new Date().toLocaleDateString(i18n.language);

  return (
    <div
      id="payslip-template"
      className="w-[210mm] mx-auto p-8 text-black"
      style={{ backgroundColor: "#ffffff", color: "#000000", fontFamily: "Arial, sans-serif", fontSize: "12px", lineHeight: "1.4", minHeight: "297mm" }}
    >
      <div className="border-b-2 border-black pb-4 mb-6 text-center">
        <h1 className="text-2xl font-bold mb-2">{companyInfo?.name || t("payslipPdf.companyNameFallback")}</h1>
        <h2 className="text-lg font-semibold mb-1">{t("payslipPdf.statement")}</h2>
        <p className="text-sm">{t("payslipPdf.dateLabel")} {currentDate}</p>
      </div>

      <div className="mb-6 border border-black">
        <div className="bg-muted p-2 border-b border-black">
          <h3 className="font-bold text-sm">{t("payslipPdf.empInfo")}</h3>
        </div>
        <div className="p-3">
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <td className="font-semibold py-1">{t("payslipPdf.name")}</td>
                <td className="py-1">{entry.employee_full_name}</td>
                <td className="font-semibold py-1">{t("payslipPdf.empId")}</td>
                <td className="py-1">{entry.employee_registration_number}</td>
              </tr>
              <tr>
                <td className="font-semibold py-1">{t("payslipPdf.department")}</td>
                <td className="py-1">{entry.employee_department || t("payslipPdf.na")}</td>
                <td className="font-semibold py-1">{t("payslipPdf.position")}</td>
                <td className="py-1">{entry.employee_job_title || t("payslipPdf.na")}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-6 border border-black">
        <div className="bg-muted p-2 border-b border-black">
          <h3 className="font-bold text-sm">{t("payslipPdf.hoursSummary")}</h3>
        </div>
        <div className="p-3">
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <td className="py-1">{t("payslipPdf.regularHours")}</td>
                <td className="py-1 text-right">{entry.total_worked_hours || 0}h</td>
                <td className="py-1">{t("payslipPdf.overtimeHours")}</td>
                <td className="py-1 text-right">{entry.overtime_hours || 0}h</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-6">
        <table className="w-full border border-black text-sm">
          <thead>
            <tr className="bg-muted">
              <th className="border-r border-black p-2 text-left font-bold">{t("payslipPdf.earnings")}</th>
              <th className="border-r border-black p-2 text-right font-bold">{t("payslipPdf.amount")}</th>
              <th className="border-r border-black p-2 text-left font-bold">{t("payslipPdf.deductions")}</th>
              <th className="p-2 text-right font-bold">{t("payslipPdf.amount")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border-r border-black border-t border-black p-2">{t("payslipPdf.baseSalary")}</td>
              <td className="border-r border-black border-t border-black p-2 text-right">{formatCurrency(entry.base_pay)}</td>
              <td className="border-r border-black border-t border-black p-2">{t("payslipPdf.totalDeductions")}</td>
              <td className="border-t border-black p-2 text-right">{formatCurrency(entry.total_deductions)}</td>
            </tr>
            <tr>
              <td className="border-r border-black border-t border-black p-2">{t("payslipPdf.overtimePay")}</td>
              <td className="border-r border-black border-t border-black p-2 text-right">{formatCurrency(entry.overtime_pay)}</td>
              <td className="border-r border-black border-t border-black p-2"></td>
              <td className="border-t border-black p-2 text-right"></td>
            </tr>
            <tr>
              <td className="border-r border-black border-t border-black p-2">{t("payslipPdf.otherAllowances")}</td>
              <td className="border-r border-black border-t border-black p-2 text-right">{formatCurrency(entry.night_differential || 0)}</td>
              <td className="border-r border-black border-t border-black p-2"></td>
              <td className="border-t border-black p-2 text-right"></td>
            </tr>
            <tr className="bg-muted font-bold">
              <td className="border-r border-black border-t border-black p-2">{t("payslipPdf.grossPay")}</td>
              <td className="border-r border-black border-t border-black p-2 text-right">{formatCurrency(entry.gross_pay)}</td>
              <td className="border-r border-black border-t border-black p-2">{t("payslipPdf.totalDeductionsCaps")}</td>
              <td className="border-t border-black p-2 text-right">{formatCurrency(entry.total_deductions)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mb-6 border-2 border-black">
        <div className="bg-gray-200 p-4 text-center">
          <h3 className="text-lg font-bold mb-2">{t("payslipPdf.netPay")}</h3>
          <p className="text-2xl font-bold">{formatCurrency(entry.net_pay)}</p>
        </div>
      </div>

      <div className="mt-8 pt-4 border-t border-gray-400 text-xs text-center">
        <p>{t("payslipPdf.footer1")}</p>
        <p>{t("payslipPdf.generatedOn", { date: new Date().toLocaleString(i18n.language) })}</p>
      </div>
    </div>
  );
};

const PayslipPDFModal = ({ isOpen, onClose, entry, companyInfo, formatCurrency }) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async () => {
    if (!entry) return;
    try {
      setIsGenerating(true);
      const pdf = generateTunisiaPayslipPdf({
        entries: [entry],
        companyInfo,
        selectedPeriod: entry.period_start && entry.period_end
          ? { start_date: entry.period_start, end_date: entry.period_end }
          : null,
        formatCurrency,
        t,
      });
      const filename = `payslip-${entry.employee_registration_number}-${new Date().toISOString().split("T")[0]}.pdf`;
      pdf.save(filename);
    } catch (error) {
      toast.error(t("payslipPdf.errorGen"));
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen || !entry) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <FileDown className="h-5 w-5" />
            {t("payslipPdf.modalTitle")} - {entry.employee_full_name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-4 h-[70vh]">
          <div className="w-64 space-y-4">
            <Card className="p-4">
              <h4 className="font-semibold mb-3">{t("payslipPdf.pdfOptions")}</h4>
              <div className="space-y-4">
                <Button onClick={generatePDF} disabled={isGenerating} className="w-full">
                  {isGenerating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("payslipPdf.generating")}</>
                  ) : (
                    <><FileDown className="h-4 w-4 mr-2" />{t("payslipPdf.generatePdf")}</>
                  )}
                </Button>
                <Button variant="outline" onClick={onClose} className="w-full">{t("common.cancel")}</Button>
              </div>
            </Card>
          </div>
          <div className="flex-1 bg-muted rounded-lg p-4 overflow-y-auto">
            <div className="rounded-lg shadow-lg transform scale-75 origin-top-left" style={{ backgroundColor: "#ffffff" }}>
              <SimplePayslipTemplate entry={entry} companyInfo={companyInfo} formatCurrency={formatCurrency} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PayslipPDFModal;
